<template>
  <div
    class="home tw-mx-auto tw-flex tw-flex-col tw-flex-1 tw-h-full tw-relative tw-overflow-x-hidden"
    @dragenter.prevent="setDrugingFile(true)"
    @drop="onDrugFile"
    @dragover.prevent="dragover"
  >
    <PtHeader />
    <DropFileCard
      v-if="isDrugingFile"
      full-height
      class="drop-file tw-absolute"
      @dragleave.prevent="setDrugingFile(false)"
    />
    <router-view />
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'

import DropFileCard from '@shared/components/DropFileCard.vue'
import { useModal } from '@shared/composables/use-modal'
import { initTheme } from '@shared/plugins/theme'
import { ConversationStatus } from '@shared/proto-ts/qliq_protobuf/conversation_enums_pb'
import { useChatInputActions } from '@shared/utils/composables/chat-messages'

import PtHeader from '@pc/components/PtHeader.vue'
import { useMessagePayload } from '@pc/composables/use-message-payload'
import { useConversationsStore } from '@pc/store/conversations'
import { useSessionStore } from '@pc/store/session'

const sessionStore = useSessionStore()
const { chatProps } = storeToRefs(sessionStore)
const conversationsStore = useConversationsStore()
const { currentConversation } = storeToRefs(conversationsStore)
const { botPayload } = useMessagePayload()

const currentWidget = computed(() => chatProps.value?.chatWidget)
const maxSize = computed(() => currentWidget.value?.maximumAttachmentSize)
const allowedTypes = computed(
  () => currentWidget.value?.restrictAttachmentTypes
)

const isAbleToSendAttachments = computed(() => {
  return !currentWidget.value?.disableChatAttachments
})

const isGpt = computed(() => {
  const isGptCase = botPayload.value?.option?.case === 'gpt'

  return (
    currentConversation.value?.status === ConversationStatus.ASSIGNED_TO_BOT &&
    isGptCase
  )
})

const widget = computed(() => sessionStore.chatProps?.chatWidget)
const widgetBrandingColor = computed(() => widget.value?.branding?.data)

const { drugFile } = useChatInputActions()
const { isVisible: isDrugingFile, setVisible } = useModal()

const setDrugingFile = (value: boolean) => {
  if (isAbleToSendAttachments.value) {
    setVisible(value)
  }
}
const dragover = () => {
  if (!isDrugingFile.value) {
    setDrugingFile(true)
  }
}

const onDrugFile = (event: DragEvent) => {
  if (isAbleToSendAttachments.value) {
    setDrugingFile(false)
    drugFile(
      event,
      {
        maxSize: maxSize.value,
        allowedTypes: allowedTypes.value,
      },
      isGpt.value
    )
  }
}

watch(
  () => widgetBrandingColor.value,
  (val) => {
    if (val) {
      initTheme(val)
    }
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.home {
  box-shadow: 0 7px 40px 2px rgba(148, 149, 150, 0.1);
  max-width: $max-patient-width;
  @apply tw-w-full tw-overflow-y-auto;
}

.drop-file {
  top: $patient-header-height;
  height: calc(100% - ($patient-header-height)) !important;
}
</style>
